import { IField } from '../../form-root/contracts';
import { ConditionalDefinitions } from '../form-block/conditionals/contracts';

export type ProvidesSubQuestion = {
	name: string;
	id: string;
	type: string;
	selects?: ProvidesSelect;
}[];

export type BlockProvides = Record<string, ProvidesSelect | ProvidesSubQuestion>;

export type QuestionBlockProps = Omit<IField, 'type'> & {
	type: FormQuestionTypes;
	description?: string;
};

export enum TextDisplayTypes {
	singleLine = 'Single line',
	textArea = 'Text area'
}

export type QuestionDisplayType = 'dropdown' | 'checkbox' | 'radio' | 'toggle' | 'buttons' | TextDisplayTypes;
export type QuestionChoice = {
	displayName?: string;
	value?: string;
	branchId?: string;
	id?: string;
};

export type QuestionBlockMetaColumns = {
	name: string;
	id: string;
	required?: boolean;
	type: QuestionBlockDataTableCellTypes;
	deletedAt?: string;
}[];

export enum QuestionBlockDataTableCellTypes {
	text = 'text',
	number = 'number'
}

/**
 * Metadata for a question block.
 * This defines properties, some shared and some unique to question types in the question block
 */
export const DEFAULT_POINT_TYPE = 'QuizPoints';

export type QuestionBlockMetadata = {
	choices?: QuestionChoice[];
	questions?: string[];
	questionIds?: { name: string; id: string }[];
	required?: boolean;
	selectAll?: boolean;
	orientation?: 'horizontal' | 'vertical';
	displayType?: QuestionDisplayType;
	numberOfQuestions?: number;
	ratingDescriptionLeft?: string;
	ratingDescriptionRight?: string;
	conditionalDefinitions?: ConditionalDefinitions;
	provides?: BlockProvides;
	maxLength?: number | null;
	columns?: QuestionBlockMetaColumns;
	maxValue?: number;
	points?: {
		/**
		 * Controls whether the point edit input is shown
		 */
		enablePoints: boolean;
		/**
		 * Determines how points are awarded:
		 * - `'match'`: The answer must exactly match the choice. E.g answer C has 10 points set, only awarded 10 points if the answer selected is answer C
		 * - `'equivalent'`: A direct transfer of value entered → points given. E.g number field, enter 15, get 15 points
		 */
		pointMatching: 'match' | 'equivalent';
		/**
		 * Defines how points are assigned for each possible answer choice.
		 * - `requiredValue`: Optional identifier, e.g. for single/multi select question, the way we identify the answer choice.
		 * - `pointValue`: The number of points assigned for this choice.
		 * - `pointType`: A future-proof property to allow different point types in the future with a little bit of additional work.
		 */
		pointMappings: { requiredValue?: string; pointValue?: string; pointType: typeof DEFAULT_POINT_TYPE }[];
	};
	/**
	 * Configuration for handling default answers in the block.
	 * Allows for automatic answer generation and re-answer behavior.
	 *
	 * @param build - If true, a default answer will be built automatically for this question.
	 * @param shouldReAnswer - If true, any existing answer will be overwritten with the newly build default answer on answer changes. This is useful when default answer is built based on different answers/conditionally
	 */
	defaultAnswer?: {
		build?: boolean;
		shouldReAnswer?: boolean;
	};
	/**
	 * @param hidden - Hides the question from the user but will still exist in the form. Useful when combined with default answers
	 */
	hidden?: boolean;
};

export type ProvidesSelect = { id: string; name: string }[];

export enum FormQuestionTypes {
	Date = 'Date',
	Email = 'Email',
	Phone = 'Phone',
	Mobile = 'Mobile',
	Number = 'Number',
	Address = 'Address',
	MatrixScaleChoice = 'MatrixScaleChoice',
	SingleChoiceRating = 'SingleChoiceRating',
	SingleChoice = 'SingleChoice',
	Text = 'Text',
	SingleChoiceWithOther = 'SingleChoiceWithOther',
	MultiChoice = 'MultiChoice',
	MultiChoiceWithOther = 'MultiChoiceWithOther',
	DataTable = 'DataTable'
}

export const SELECTALL_DISPLAY_TYPES = ['checkbox', 'radio'];
export const SELECTALL_FIELD_TYPES = [FormQuestionTypes.MultiChoice, FormQuestionTypes.MultiChoiceWithOther];

export const FormQuestionFieldTypes = [
	{ id: FormQuestionTypes.Text, name: 'Text' },
	{ id: FormQuestionTypes.SingleChoice, name: 'Single Choice' },
	{ id: FormQuestionTypes.SingleChoiceWithOther, name: 'Single Choice (with other)' },
	{ id: FormQuestionTypes.MultiChoice, name: 'Multi Choice' },
	{ id: FormQuestionTypes.MultiChoiceWithOther, name: 'Multi Choice (with other)' },
	{ id: FormQuestionTypes.SingleChoiceRating, name: 'Single Choice Rating' },
	{ id: FormQuestionTypes.Date, name: 'Date' },
	{ id: FormQuestionTypes.Number, name: 'Number' },
	{ id: FormQuestionTypes.Mobile, name: 'Mobile' },
	{ id: FormQuestionTypes.Email, name: 'Email' },
	{ id: FormQuestionTypes.Phone, name: 'Phone' },
	{ id: FormQuestionTypes.Address, name: 'Address' },
	{ id: FormQuestionTypes.MatrixScaleChoice, name: 'Matrix Scale Choice' },
	{ id: FormQuestionTypes.DataTable, name: 'Data Table' }
] as const;

export const ShowChoiceSettings = [
	FormQuestionTypes.SingleChoice,
	FormQuestionTypes.SingleChoiceWithOther,
	FormQuestionTypes.MultiChoice,
	FormQuestionTypes.MultiChoiceWithOther,
	FormQuestionTypes.SingleChoiceRating,
	FormQuestionTypes.MatrixScaleChoice
];
export const ShowOrientationTypes = [
	FormQuestionTypes.SingleChoice,
	FormQuestionTypes.SingleChoiceWithOther,
	FormQuestionTypes.MultiChoice,
	FormQuestionTypes.MultiChoiceWithOther
];

export const DisplayTypes = [
	{ type: 'dropdown', display: 'Dropdown' },
	{ type: 'checkbox', display: 'Checkboxes' },
	{ type: 'radio', display: 'Radio buttons' },
	{ type: 'toggle', display: 'Toggle buttons' },
	{ type: 'buttons', display: 'Separate buttons' }
];
